"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useUser } from "@clerk/nextjs";
import { BookingSignupModal } from "@/components/booking/BookingSignupModal";
import { DemoRequestModal } from "@/components/booking/DemoRequestModal";
import { ContactRequestModal } from "@/components/booking/ContactRequestModal";
import { buildBookingDeepLink } from "@/lib/booking-url";
import { createGuestCheckout } from "@/app/gaseste-terapeut/actions";
import { trackEvent } from "@/lib/analytics";

interface Slot {
  startTime: string;
  endTime: string;
}

interface Props {
  slots: Slot[];
  therapistId: string;
  therapistFirstName?: string | null;
  therapistAvatarUrl?: string | null;
  /** When the therapist offers a free intro, the slots book that (consistent
   *  with everywhere else) instead of going to the paid checkout. */
  acceptsDiscoverySessions?: boolean;
  /** kept for backwards-compat; unused by signup-modal path. */
  appUrl?: string;
}

export default function AvailabilitySlots({
  slots,
  therapistId,
  therapistFirstName,
  therapistAvatarUrl,
  acceptsDiscoverySessions = false,
}: Props) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const viewerTz = mounted ? Intl.DateTimeFormat().resolvedOptions().timeZone : "";
  const router = useRouter();

  // Slots are fetched server-side with `cache: "no-store"` on a force-dynamic
  // page, so every fresh request already gets live data — but a tab left open
  // (or restored from bfcache/Router Cache) never refetches on its own.
  // Force a re-render with fresh server data whenever the tab regains focus,
  // so a client who left this page open sees the therapist's latest slots
  // instead of whatever was available when they first loaded it.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") router.refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
    };
  }, [router]);
  const [bookingSlot, setBookingSlot] = useState<string | null>(null);
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoPreselect, setDemoPreselect] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { isSignedIn, isLoaded } = useUser();

  // Deep-link from the /terapeuti directory slot chips: ?slot=<iso> opens the
  // discovery-session dialog with that time pre-ticked. Only for therapists
  // who offer the free intro — paid booking stays an explicit click.
  const searchParams = useSearchParams();
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;
    const slotParam = searchParams.get("slot");
    if (!slotParam || !acceptsDiscoverySessions) return;
    deepLinkHandled.current = true;
    trackEvent("demo_request_open", {
      source: "terapeuti_directory_slot",
      therapist_id: therapistId,
      slot: slotParam,
    });
    setDemoPreselect(slotParam);
    setDemoOpen(true);
  }, [searchParams, acceptsDiscoverySessions, therapistId]);

  const onSlotClick = async (isoStart: string) => {
    trackEvent("therapist_profile_slot_clicked", {
      therapist_id: therapistId,
      slot: isoStart,
    });
    // Therapist offers a free intro → these slots book the discuție de
    // cunoaștere (20 min, gratuit), same as the dashboard and the app.
    if (acceptsDiscoverySessions) {
      trackEvent("demo_request_open", {
        source: "therapist_profile_slot",
        therapist_id: therapistId,
      });
      setDemoPreselect(isoStart);
      setDemoOpen(true);
      return;
    }
    // Signed in → book in the app. Guest → fast guest checkout with this slot.
    if (isLoaded && isSignedIn) {
      window.location.href = buildBookingDeepLink({
        therapistId,
        isoStart,
        source: "therapist_profile",
      });
      return;
    }
    setBusy(true);
    const r = await createGuestCheckout({ therapistId });
    if ("url" in r) {
      const sep = r.url.includes("?") ? "&" : "?";
      const target = `${r.url}${sep}slot=${encodeURIComponent(isoStart)}`;
      router.push(`/guest-redirect?to=${encodeURIComponent(target)}`);
    } else {
      // Fallback to the signup modal if guest checkout can't start.
      setBusy(false);
      setBookingSlot(isoStart);
    }
  };

  const slotsByDay = slots.reduce<Record<string, Slot[]>>((acc, slot) => {
    const key = new Date(slot.startTime).toLocaleDateString("ro-RO", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
    (acc[key] ??= []).push(slot);
    return acc;
  }, {});

  return (
    <div>
      {acceptsDiscoverySessions && (
        <p className="mb-2.5 flex items-center gap-1.5 text-[13px] font-medium text-[#C9542F]">
          <span className="size-1.5 rounded-full bg-[#DC7050]" aria-hidden />
          Alege o oră pentru ședința de cunoaștere gratuită · 20 min
        </p>
      )}
      <p className="mb-4 h-4 text-[12.5px] text-[#264653]/45" suppressHydrationWarning>
        {mounted ? `Ore afișate în fusul tău orar · ${viewerTz}` : ""}
      </p>
    {!mounted ? (
      <div className="space-y-4">
        {[0, 1].map((g) => (
          <div key={g} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
            <div className="h-3.5 w-20 animate-pulse rounded bg-[#264653]/10 sm:w-24" />
            <div className="flex flex-wrap gap-2">
              {[0, 1, 2].map((s) => (
                <div
                  key={s}
                  className="h-[30px] w-24 animate-pulse rounded-md bg-[#264653]/8"
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    ) : (
      <div className="space-y-4">
        {Object.entries(slotsByDay).map(([day, daySlots]) => (
          <div key={day} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
            <span className="shrink-0 pt-1.5 text-[12px] font-semibold uppercase tracking-[0.18em] text-[#264653]/50 sm:w-32">
              {day}
            </span>
            <div className="flex flex-wrap gap-2">
              {daySlots.map((slot, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={busy}
                  onClick={() => onSlotClick(slot.startTime)}
                  className={`inline-flex cursor-pointer items-center rounded-md border px-3 py-1.5 text-[12.5px] font-medium transition-colors disabled:opacity-50 ${
                    acceptsDiscoverySessions
                      ? "border-[#DC7050]/35 text-[#C9542F] hover:border-[#DC7050] hover:bg-[#DC7050]/6"
                      : "border-[#264653]/18 text-[#264653]/80 hover:border-[#264653]/45 hover:text-[#264653]"
                  }`}
                >
                  {new Date(slot.startTime).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                  {" – "}
                  {new Date(slot.endTime).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    )}

      {acceptsDiscoverySessions && (
        <button
          type="button"
          onClick={() => {
            trackEvent("contact_request_open", {
              source: "therapist_profile_slot_fallback",
              therapist_id: therapistId,
            });
            setContactOpen(true);
          }}
          className="mt-4 flex w-full items-center justify-center gap-1 rounded-xl bg-[#DC7050]/10 px-3 py-2.5 text-[13px] font-semibold text-[#C9542F] transition hover:bg-[#DC7050]/15 sm:w-auto"
        >
          Nicio oră potrivită? Cere o altă oră →
        </button>
      )}

    <BookingSignupModal
      open={!!bookingSlot}
      onClose={() => setBookingSlot(null)}
      therapistId={therapistId}
      therapistFirstName={therapistFirstName ?? null}
      therapistAvatarUrl={therapistAvatarUrl ?? null}
      isoStart={bookingSlot ?? new Date().toISOString()}
      source="therapist-profile"
    />

    <DemoRequestModal
      open={demoOpen}
      onClose={() => setDemoOpen(false)}
      therapistId={therapistId}
      therapistFirstName={therapistFirstName ?? null}
      availabilitySlots={slots}
      preselectStart={demoPreselect}
      source="terapeut_profile_slot"
    />

    <ContactRequestModal
      open={contactOpen}
      onClose={() => setContactOpen(false)}
      therapistId={therapistId}
      therapistFirstName={therapistFirstName ?? null}
      source="terapeut_profile_slot"
    />
    </div>
  );
}
